import { useEffect, useRef } from 'react';

interface IuseInfiniteScroll {
  onLoadMore: () => void;
  hasMore: boolean;
  loading: boolean;
  config?: IntersectionObserverInit;
}

/**
 * Hook para implementar infinite scroll usando IntersectionObserver.
 *
 * Retorna una ref que debe asignarse al elemento "sentinela".
 * Cuando este elemento entra en el viewport, se ejecuta `onLoadMore`
 * si `hasMore` es true y `loading` es false.
 *
 * @example
 * const { ref } = useInfiniteScroll({
 *   onLoadMore: fetchMore,
 *   hasMore,
 *   loading,
 *   config: { rootMargin: '100px' }
 * });
 */
export const useInfiniteScroll = ({
  onLoadMore,
  hasMore,
  loading,
  config = {
    root: null,
    rootMargin: '200px',
    threshold: 0
  }
}: IuseInfiniteScroll) => {
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!ref.current) return;

    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];

      if (entry.isIntersecting && hasMore && !loading) {
        onLoadMore();
      }
    }, config);

    observer.observe(ref.current);

    return () => {
      observer.disconnect();
    };
  }, [onLoadMore, hasMore, loading]);

  return { ref };
};
